// Real internal team chat — DMs + tenant-wide group thread, real-time via WS.
//
// Behaviour:
//   - Poll /threads every 8s for thread list + unread totals (badge).
//   - When a thread is open, poll its messages every 4s AND listen to WS
//     'internal_chat.new_message' events from the existing /api/ws/agent socket.
//   - "Chat" on an agent → DM thread; "Team" entry → group thread.
//
import { useEffect, useRef, useState, Fragment } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send, ArrowLeft, Users, Loader2, Hash } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Dot colour per runtime status — mirrors the Live Dashboard state palette.
const STATUS_DOT = {
  available: "bg-emerald-500", ready: "bg-emerald-500",
  talking: "bg-red-500", held: "bg-red-500", originating: "bg-red-500",
  dead_call: "bg-red-500",
  ringing: "bg-amber-500",
  wrap: "bg-violet-500",
  paused: "bg-amber-500", break: "bg-amber-500", lunch: "bg-amber-500", manual: "bg-amber-500",
  offline: "bg-slate-400",
};
const DIGITAL_STATES = new Set(["whatsapp", "sms", "email", "webchat", "instagram", "facebook", "telegram", "voicemail"]);
const STATUS_LABEL = {
  available: "Available", ready: "Ready", talking: "In Call", held: "On Hold",
  originating: "Dialing", dead_call: "Dead Call", ringing: "Ringing",
  wrap: "Wrap-up", paused: "Paused", break: "Break", lunch: "Lunch",
  manual: "Manual Dial", offline: "Offline",
  whatsapp: "WhatsApp", sms: "SMS", email: "Email", webchat: "Web Chat",
  instagram: "Instagram", facebook: "Facebook", telegram: "Telegram", voicemail: "Voicemail",
};
function statusDot(s) { return DIGITAL_STATES.has(s) ? "bg-sky-500" : (STATUS_DOT[s] || "bg-slate-400"); }
function statusLabel(s) { return STATUS_LABEL[s] || (s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Offline"); }
// Legacy 4-value map kept for the DM-thread peer dot (peer.presence carries the
// runtime status string now).
const presenceColor = STATUS_DOT;

const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const WS_BASE = RAW_BACKEND.replace(/^http/, "ws");

export default function TeamChatPopover() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [groupThreadId, setGroupThreadId] = useState(null);
  const [agents, setAgents] = useState([]);
  const [activeThread, setActiveThread] = useState(null); // {id, kind, peer?, name?}
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const scrollRef = useRef(null);
  const wsRef = useRef(null);

  // ---- Load initial threads + agent roster ----
  async function refreshThreads() {
    try {
      const { data } = await api.get("/internal-chat/threads");
      setThreads(data.threads || []);
      setTotalUnread(data.total_unread || 0);
      setGroupThreadId(data.group_thread_id || null);
    } catch (_) {}
  }
  async function refreshAgents() {
    try {
      const { data } = await api.get("/agents");
      setAgents((data || []).filter((a) => a.id !== user?.id));
    } catch (_) {}
  }

  // Poll every 8s for thread list (unread badge) AND the agent roster so
  // presence/status stays live — mirrors how the Live Dashboard refreshes.
  // Without polling the roster, statuses froze at mount (a logged-out agent
  // kept showing green).
  useEffect(() => {
    if (!user) return;
    refreshThreads(); refreshAgents();
    const id = setInterval(() => { refreshThreads(); refreshAgents(); }, 8000);
    const onVis = () => { if (document.visibilityState === "visible") { refreshThreads(); refreshAgents(); } };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ---- WebSocket subscription for real-time incoming messages ----
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("c7_token");
    if (!token) return;
    let stopped = false;
    let reconnectTimer = null;

    function connect() {
      if (stopped) return;
      const ws = new WebSocket(`${WS_BASE}/api/ws/agent?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        try {
          const m = JSON.parse(e.data);
          // Agent state changed somewhere on the floor → refresh the roster so
          // presence dots update in near-real-time (debounced by the 8s poll).
          if (m.type === "agent.state" || m.type === "agent.presence") {
            refreshAgents();
            return;
          }
          if (m.type !== "internal_chat.new_message") return;
          // Only care if I'm a participant: tenant-wide group (everyone), a
          // group channel I belong to, OR a DM I'm in.
          const parts = m.participant_ids || [];
          const isChannel = m.thread_kind === "group" && parts.length > 0;
          const isMine = (m.thread_kind === "group" && !isChannel) || parts.includes(user.id);
          if (!isMine) return;
          // If the message is for the open thread, append it
          if (activeThreadRef.current && activeThreadRef.current.id === m.thread_id) {
            setMessages((prev) => prev.some((x) => x.id === m.message.id) ? prev : [...prev, m.message]);
            // auto-mark read since panel is open
            api.post(`/internal-chat/threads/${m.thread_id}/read`).catch(() => {});
          }
          // Always refresh the thread list for the badge / previews
          refreshThreads();
        } catch (_) {}
      };
      ws.onclose = () => {
        if (!stopped) reconnectTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => { try { ws.close(); } catch (_) {} };
    }
    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ---- Track activeThread in a ref for the WS handler ----
  const activeThreadRef = useRef(null);
  useEffect(() => { activeThreadRef.current = activeThread; }, [activeThread]);

  // ---- Load messages whenever active thread changes; poll every 4s ----
  useEffect(() => {
    if (!activeThread) return;
    let alive = true;
    async function load() {
      try {
        const { data } = await api.get(`/internal-chat/threads/${activeThread.id}/messages`);
        if (!alive) return;
        setMessages(data.messages || []);
      } catch (_) {}
    }
    load();
    // mark read
    api.post(`/internal-chat/threads/${activeThread.id}/read`).then(refreshThreads).catch(() => {});
    const id = setInterval(load, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [activeThread?.id]);

  // Autoscroll to latest
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, activeThread?.id]);

  async function openDm(peerId) {
    try {
      const { data } = await api.post("/internal-chat/threads/dm", { peer_id: peerId });
      setActiveThread({ id: data.id, kind: "dm", peer: data.peer });
    } catch (_) {}
  }
  function openGroup() {
    if (!groupThreadId) return;
    setActiveThread({ id: groupThreadId, kind: "group", name: "Team" });
  }
  function openChannel(t) {
    setActiveThread({ id: t.id, kind: "group", name: t.name || "Channel" });
  }

  const isOnline = (a) => (a.runtime_state || a.chat_status || a.presence) !== "offline";
  const channels = threads.filter((t) => t.kind === "group" && t.group_id);
  const visibleAgents = agents
    .filter((a) => !search || (a.name || "").toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => {
      const ao = isOnline(a) ? 0 : 1, bo = isOnline(b) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      return (a.name || "").localeCompare(b.name || "");
    });

  async function send() {
    if (!msg.trim() || sending || !activeThread) return;
    setSending(true);
    const body = msg.trim();
    setMsg("");
    try {
      const { data } = await api.post(`/internal-chat/threads/${activeThread.id}/messages`, { body });
      setMessages((prev) => prev.some((x) => x.id === data.id) ? prev : [...prev, data]);
      refreshThreads();
    } catch (_) {
      setMsg(body); // restore on failure
    } finally {
      setSending(false);
    }
  }

  function fmtTime(iso) {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = new Date();
      const sameDay = d.toDateString() === now.toDateString();
      return sameDay
        ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString([], { month: "short", day: "numeric" });
    } catch (_) { return ""; }
  }
  function fmtDay(iso) {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = new Date();
      const yest = new Date(now); yest.setDate(now.getDate() - 1);
      if (d.toDateString() === now.toDateString()) return "Today";
      if (d.toDateString() === yest.toDateString()) return "Yesterday";
      return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
    } catch (_) { return ""; }
  }

  const onlineCount = agents.filter((a) => (a.runtime_state || a.chat_status || a.presence) !== "offline").length;

  return (
    <div className="relative w-11 h-11">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="open-team-chat-btn"
            title="Team chat"
            className="w-11 h-11 rounded-full grid place-items-center bg-card border border-border text-foreground shadow-md hover:bg-muted transition-colors"
          >
            <MessageSquare className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </PopoverTrigger>

        <PopoverContent
          side="top"
          align="start"
          sideOffset={12}
          className="w-[360px] p-0 overflow-hidden bg-card border-border z-[60]"
          data-testid="team-chat-popup"
        >
          <div className="px-4 py-3 bg-secondary text-secondary-foreground flex items-center gap-2">
            {activeThread ? (
              <>
                <button data-testid="chat-back-btn" onClick={() => setActiveThread(null)} className="hover:opacity-80">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-2 min-w-0">
                  {activeThread.kind === "dm" && activeThread.peer ? (
                    <>
                      <div className="relative">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback style={{ background: activeThread.peer.avatar_color, color: "white" }} className="text-[10px] font-bold">
                            {activeThread.peer.initials}
                          </AvatarFallback>
                        </Avatar>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-secondary ${statusDot(activeThread.peer.presence)}`} />
                      </div>
                      <div className="font-display font-bold tracking-tight text-sm truncate">{activeThread.peer.name}</div>
                    </>
                  ) : (
                    <>
                      <Users className="w-4 h-4" />
                      <div className="font-display font-bold tracking-tight text-sm truncate">{activeThread.name || "Team thread"}</div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <MessageSquare className="w-4 h-4" />
                <div className="font-display font-bold tracking-tight text-sm">Team Chat</div>
                <Badge className="ml-auto bg-emerald-500/20 text-emerald-300 border-0 text-[10px]" data-testid="online-count-pill">
                  {onlineCount} online
                </Badge>
              </>
            )}
          </div>

          {/* ----- LIST VIEW ----- */}
          {!activeThread && (
            <>
              {/* Search */}
              <div className="px-3 pt-2.5 pb-1">
                <Input
                  data-testid="chat-search-input"
                  placeholder="Search people…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>

              {/* Group thread row */}
              {groupThreadId && (
                <button
                  data-testid="chat-open-group"
                  onClick={openGroup}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 border-b border-border hover:bg-muted/60"
                >
                  <div className="w-7 h-7 rounded-full grid place-items-center bg-secondary/15 text-secondary">
                    <Users className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium">Team thread</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {threads.find((t) => t.id === groupThreadId)?.last_message_preview || "Tenant-wide channel"}
                    </div>
                  </div>
                  {(() => {
                    const t = threads.find((x) => x.id === groupThreadId);
                    return t?.unread_count > 0 ? (
                      <Badge className="bg-primary text-primary-foreground text-[10px]" data-testid="chat-group-unread">
                        {t.unread_count}
                      </Badge>
                    ) : null;
                  })()}
                </button>
              )}

              {/* Group channels */}
              {channels.length > 0 && (
                <div className="px-3 pt-2">
                  <div className="text-overline text-muted-foreground mb-1 px-1">Channels</div>
                  <ul className="space-y-0.5">
                    {channels.map((c) => (
                      <li key={c.id}>
                        <button
                          data-testid={`chat-open-channel-${c.group_id}`}
                          onClick={() => openChannel(c)}
                          className="w-full text-left flex items-center gap-2 px-1 py-1 rounded hover:bg-muted"
                        >
                          <div className="w-7 h-7 rounded-full grid place-items-center bg-primary/10 text-primary">
                            <Hash className="w-3.5 h-3.5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium truncate">{c.name}</div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {c.last_message_preview || "Group channel"}
                            </div>
                          </div>
                          {c.unread_count > 0 && (
                            <Badge className="bg-primary text-primary-foreground text-[10px]" data-testid={`chat-channel-unread-${c.group_id}`}>
                              {c.unread_count}
                            </Badge>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Agent list — online first */}
              <div className="px-3 py-2">
                <div className="text-overline text-muted-foreground mb-1.5 px-1">Direct messages</div>
                <ul className="space-y-0.5 max-h-[240px] overflow-y-auto" data-testid="chat-people-list">
                  {visibleAgents.length === 0 ? (
                    <li className="text-xs text-muted-foreground px-1 py-2">
                      {search ? "No people match your search." : "No other users in this tenant."}
                    </li>
                  ) : (
                    visibleAgents.map((a) => {
                      const dm = threads.find((t) => t.kind === "dm" && t.peer?.id === a.id);
                      const unread = dm?.unread_count || 0;
                      return (
                        <li key={a.id} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted">
                          <div className="relative">
                            <Avatar className="h-7 w-7">
                              <AvatarFallback style={{ background: a.avatar_color, color: "white" }} className="text-[10px] font-bold">
                                {a.initials}
                              </AvatarFallback>
                            </Avatar>
                            <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-card ${statusDot(a.runtime_state || a.chat_status || a.presence)}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium truncate">{a.name}</div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {dm?.last_message_preview || statusLabel(a.runtime_state || a.chat_status || a.presence)}
                            </div>
                          </div>
                          {unread > 0 && (
                            <Badge className="bg-primary text-primary-foreground text-[10px]" data-testid={`chat-dm-unread-${a.id}`}>
                              {unread}
                            </Badge>
                          )}
                          <Button
                            data-testid={`chat-open-dm-${a.id}`}
                            onClick={() => openDm(a.id)}
                            size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                          >
                            Chat
                          </Button>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            </>
          )}

          {/* ----- THREAD VIEW ----- */}
          {activeThread && (
            <>
              <ScrollArea className="h-[280px] px-3" ref={scrollRef}>
                <ul className="space-y-1.5 py-2">
                  {messages.length === 0 ? (
                    <li className="text-center text-xs text-muted-foreground py-8">
                      No messages yet — say hello.
                    </li>
                  ) : (
                    messages.map((m, i) => {
                      const self = m.sender_id === user?.id;
                      const prev = messages[i - 1];
                      const showDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
                      return (
                        <Fragment key={m.id}>
                          {showDay && (
                            <li className="flex justify-center py-1" data-testid="chat-day-sep">
                              <span className="text-[9px] font-medium text-muted-foreground bg-muted/60 rounded-full px-2 py-0.5">
                                {fmtDay(m.created_at)}
                              </span>
                            </li>
                          )}
                          <li className={`flex ${self ? "justify-end" : ""}`}>
                            <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-xs ${self ? "bg-secondary text-secondary-foreground" : "bg-muted"}`}>
                              {!self && activeThread.kind === "group" && (
                                <div className="text-[9px] uppercase tracking-wider opacity-70 mb-0.5">
                                  {m.sender_name}
                                </div>
                              )}
                              <div>{m.body}</div>
                              <div className={`text-[9px] mt-0.5 ${self ? "opacity-70" : "text-muted-foreground"}`}>
                                {fmtTime(m.created_at)}
                              </div>
                            </div>
                          </li>
                        </Fragment>
                      );
                    })
                  )}
                </ul>
              </ScrollArea>
              <div className="p-2 border-t border-border flex gap-1.5">
                <Input
                  data-testid="team-chat-input"
                  placeholder="Type a message…"
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  className="h-8 text-xs"
                  disabled={sending}
                />
                <Button
                  data-testid="team-chat-send"
                  onClick={send}
                  size="icon" className="h-8 w-8"
                  disabled={sending || !msg.trim()}
                >
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>

      {totalUnread > 0 && (
        <span
          data-testid="team-chat-unread-badge"
          className="pointer-events-none absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none ring-2 ring-card shadow-sm"
        >
          {totalUnread > 99 ? "99+" : totalUnread}
        </span>
      )}
    </div>
  );
}
